/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { Context as CompanyContext } from 'contexts/company';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { pushHandlerSingleton } from 'services/oneSignal';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { updateWebPushDevice } from 'api';
import { Config } from 'configs';
import { useAggregate } from 'hooks';
import { Loader } from 'components/indicators/loader';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { DropDownList } from 'components/formInputs/dropDownList';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';

export const WebNotificationsEdit = () => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);
  const { state: companyState, getCompanyRestaurants } = useContext(CompanyContext);

  const [refresher, setRefresher] = useState(0);
  const [isSavingOptions, setIsSavingOptions] = useState(false);
  const [isCompanyDeviceSubscribed, setIsCompanyDeviceSubscribed] = useState(false);
  const [playNotifications, setPlayNotifications] = useLocalStorage(
    'web-push-play-notifications',
    false,
  );
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);

  const pushHandler = pushHandlerSingleton.handler;

  const getSettingsRequest = {
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: '90a777df-7326-4ddd-ab13-a9922ab9852f',
    secretKey: auth.token,
    filterBuilder: () => {
      return { company: auth.company?.company, deviceId: pushHandler.userId };
    },
    loadOnInit: false,
    dontRunOnInit: true,
    aggregateIdV2: 'AdminGetDevicePushSettings',
    companyIdForV2: auth.company?.company,
  };

  const [findSettings, settingsArray, isLoadingSettings] = useAggregate(getSettingsRequest);

  const schema = yup.object().shape({
    device_id: yup.string().default(null),
    order_updates: yup.boolean().default(false),
    play_audio_notification: yup.boolean().default(false),
    restaurants: yup.array().of(yup.string()).nullable().default([]),
  });

  const { register, reset, setValue, handleSubmit } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    // pushHandler.init();
  }, []);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  useEffect(() => {
    if (settingsArray && settingsArray.length > 0) {
      const settings = settingsArray[0];

      reset(settings);
      setIsCompanyDeviceSubscribed(true);
      setSelectedRestaurants(settings.restaurants || []);
    } else {
      reset({});
      setIsCompanyDeviceSubscribed(false);
      setSelectedRestaurants([]);
    }

    setValue('play_audio_notification', playNotifications);
  }, [settingsArray]);

  useEffect(() => {
    if (pushHandler.userId) {
      findSettings();
    }
  }, [refresher]);

  const updateSubscription = async (enabled, enabledOptions) => {
    setIsSavingOptions(true);

    let response;

    if (pushHandler.userId) {
      response = await updateWebPushDevice({
        alertController,
        token: auth.token,
        deviceId: pushHandler.userId,
        enabled,
        enabledOptions,
        companyId: auth.company?.company,
      });
    }

    setIsSavingOptions(false);
    return response;
  };

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-' }, ...(companyState.restaurants || [])];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  useEffect(() => {
    pushHandler.onInit = () => {
      setRefresher((prev) => prev + 1);
    };

    pushHandler.onSubscriptionUpdate = async () => {
      if (pushHandler.userId) {
        await updateSubscription(pushHandler.isSubscribed(), { order_updates: false });
        setRefresher((prev) => prev + 1);
      }
    };
  }, []);

  const onSubmit = async (data) => {
    const response = await updateSubscription(true, {
      order_updates: data.order_updates || false,
      restaurants: data.restaurants,
    });

    if (response) {
      setPlayNotifications(data.play_audio_notification);
      pushHandler.allowPlayNotification = data.play_audio_notification;
      alertController.success({ text: 'Notifications settings updated' });
    }

    setRefresher((prev) => prev + 1);
  };

  const onError = (data) => console.log(data);

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div className="mt-2">
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = companyState.restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurantData._id}>
              <SelectedItemButton
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const isSubscribed = pushHandler.isSubscribed() && isCompanyDeviceSubscribed;

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Manage notifications</h3>
            <p className="mt-2 text-sm text-gray-700">
              <span style={{ fontWeight: 600 }}>Important!</span> This will only adjust
              notifications for this device.
            </p>
          </div>

          {!isLoadingSettings && (
            <div className="grid grid-cols-6 gap-6">
              <div className=" col-span-6">
                <button
                  type="button"
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white
                focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50
                ${
                  isSubscribed
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                }`}
                  onClick={async () => {
                    if (isSubscribed) {
                      await updateSubscription(false, { order_updates: false });
                      setRefresher((prev) => prev + 1);
                    } else {
                      pushHandler.subscribe();

                      if (pushHandler.isSubscribed() && !isCompanyDeviceSubscribed) {
                        pushHandler.onSubscriptionUpdate();
                      }
                    }
                  }}
                  disabled={isSavingOptions}
                >
                  {isSubscribed ? 'Disable push notifications' : 'Enable push notifications'}
                </button>
              </div>
              {isSubscribed && (
                <>
                  <div className="col-span-6">
                    <DropDownList
                      showLabel
                      id="restaurants"
                      name="restaurants"
                      label="Subscribed restaurants"
                      subtitle="If nothing is selected - will receive updates from all restaurants. To only receive updates from some restaurants, select them here"
                      options={getRestaurantsDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      onChange={(e) => {
                        setSelectedRestaurants((oldArray) => {
                          const newArray = [...oldArray, e.target.value];

                          return newArray;
                        });
                        setValue('restaurants', [...selectedRestaurants, e.target.value]);
                      }}
                    />
                    {renderSelectedRestaurants()}
                  </div>

                  <div className="col-span-6">
                    <CheckboxWrapper
                      register={register}
                      name="order_updates"
                      title="Order updates"
                      subtitle="receive updates to orders"
                      responsiveClassName="col-span-6"
                    />
                  </div>

                  <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />
                  <div className="col-span-6">
                    <CheckboxWrapper
                      register={register}
                      name="play_audio_notification"
                      title="Play audio notifications"
                      subtitle="keeps playing notification sound until confirmation is pressed. Additionally, for this to work, tap or click anywhere on the screen everytime dashboard is opened."
                      responsiveClassName="col-span-6"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {!!isLoadingSettings && <Loader small />}
        </div>
        {isSubscribed && (
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="submit"
              className="ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              disabled={isSavingOptions}
            >
              Save
              {isSavingOptions && (
                <div className="ml-2">
                  <Loader xxs />
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};
